import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { Toast } from 'primereact/toast';
import AppModal from '../../shared/AppModal';
import { Mention } from 'primereact/mention';
import { Dropdown } from 'primereact/dropdown';
import { DndContext, closestCorners, PointerSensor, KeyboardSensor, useSensor, useSensors, DragOverlay } from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, arrayMove, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import axios from '../../utils/axios';
import { pendingClosingsQueue, projectedClosingsQueue, updateClosingDateQueue, pingClientQueue, updateCheckList, createCheckList, createCheckListHeading, appSubmission, getCustomFields, getPipelines, registeredUserList, UpdateLeadStage, sendChecklistToContact, downloadXmlFile, downloadFilesZip, createAppDoc, updateAppDoc, createComment, updateComment, deleteComment, getQueueItems, getSoftwareUsers, reorderQueue, removeQueueItem } from '../../utils/urls';
import PreviewFile from '../../components/apps/appsKanban/PreviewFile';
import DocsModal from '../../components/apps/DocsModal';
import useAppDocs from '../../components/apps/useAppDocs';
import PreviewTitle from '../../components/apps/PreviewTitle';
import AppPopup from '../../components/apps/appsKanban/AppPopup';
import FlexComponent from '../../components/FlexComponent';
import MentionOthers from '../../components/MentionOthers';
import { WORKING_ON_CLASS, WorkingOnChip, WorkingOnToggle } from '../../components/apps/workingOn/WorkingOn';
import { useAppWorkingOn } from '../../components/apps/workingOn/useAppWorkingOn';
import CallParticipant from '../../components/apps/CallParticipant';
import './pendingClosingQueue.css';

const PAGE_SIZE = 5;

function formatRelativeTime(isoString) {
    if (!isoString) return '';
    const date = new Date(isoString);
    if (isNaN(date)) return '';
    const diff = Date.now() - date.getTime();
    const mins  = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days  = Math.floor(diff / 86400000);
    if (days > 0)  return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (mins > 0)  return `${mins}m ago`;
    return 'just now';
}

function formatDisplayDate(dateStr) {
    if (!dateStr) return '—';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function daysBetween(dateStr) {
    if (!dateStr) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateStr + 'T00:00:00');
    return Math.round((target - today) / 86400000);
}

function dueInfo(dateStr) {
    const n = daysBetween(dateStr);
    if (n === null) return null;
    if (n < 0)   return { txt: `${Math.abs(n)}d overdue`, cls: 'due-over' };
    if (n === 0) return { txt: 'Due today',               cls: 'due-soon' };
    if (n <= 3)  return { txt: `Due in ${n}d`,            cls: 'due-soon' };
    return          { txt: `Due in ${n}d`,                cls: 'due-ok' };
}

function getInitials(name) {
    if (!name) return '?';
    return name.trim().split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
}

const resolveFileUrl = (url) => url?.startsWith('/') ? `https://media.bcrumbs.com${url}` : url;

function formatCurrency(val) {
    const n = parseFloat(val);
    if (isNaN(n)) return val || '—';
    if (n >= 1000000) return `$${(n / 1000000).toFixed(1).replace(/\.0$/, '')}m`;
    if (n >= 1000)    return `$${Math.round(n / 1000)}k`;
    return `$${n.toLocaleString()}`;
}

// ── Hitlist Banner ─────────────────────────────────────────
function HitlistBanner({ cards }) {
    const outstanding  = cards.reduce((n, c) => n + (c.outstanding || 0), 0);
    const overdue      = cards.filter(c => { const d = daysBetween(c.closing_date); return d !== null && d < 0; }).length;
    const clearToClose = cards.filter(c => (c.outstanding || 0) === 0).length;
    const closingSoon  = cards.filter(c => { const d = daysBetween(c.closing_date); return d !== null && d >= 0 && d <= 7; }).length;

    return (
        <div className="pcq-hitlist">
            <span className="pcq-hitlist-title">⚡ Hitlist</span>
            <div className="pcq-hitlist-stats">
                <div className="pcq-hitlist-stat"><b>{outstanding}</b><small>Items Outstanding</small></div>
                <div className="pcq-hitlist-stat"><b className="pcq-stat-danger">{overdue}</b><small>Overdue</small></div>
                <div className="pcq-hitlist-stat"><b className="pcq-stat-green">{clearToClose}</b><small>Clear to Close</small></div>
                <div className="pcq-hitlist-stat"><b className="pcq-stat-amber">{closingSoon}</b><small>Closing ≤ 7 days</small></div>
            </div>
        </div>
    );
}

// ── Checklist Column ───────────────────────────────────────
function ChecklistColumn({ card, onPing, pinging, light, userId, toast, usersList }) {
    const [showCompleted, setShowCompleted] = useState(false);
    const [localHeadings, setLocalHeadings] = useState(card.checklist_headings || []);
    const [openDrawerId, setOpenDrawerId]   = useState(null);
    const [stagedFiles, setStagedFiles]     = useState({});
    const [removedIds, setRemovedIds]       = useState({});
    const [saving, setSaving]               = useState(null);
    const [previewFile, setPreviewFile]     = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [docsVisible, setDocsVisible]     = useState(false);
    const [editingItemId, setEditingItemId]     = useState(null);
    const [editingItemValue, setEditingItemValue] = useState('');
    const [noteItemId, setNoteItemId]           = useState(null);
    const [noteText, setNoteText]               = useState('');
    const [savingNote, setSavingNote]           = useState(false);
    const [sendMode, setSendMode]               = useState(null); // null | 'all' | 'item'
    const [sendItem, setSendItem]               = useState(null);
    const [sendChannel, setSendChannel]         = useState('Email');
    const [sendRecipients, setSendRecipients]   = useState([]);
    const [sendMessage, setSendMessage]         = useState('');
    const [sendPickedItems, setSendPickedItems] = useState([]);
    const [sendLoading, setSendLoading]         = useState(false);
    const [zipLoading, setZipLoading]           = useState(false);
    const [docViewMode,    setDocViewMode]    = useState('list');
    const [renamingFileId, setRenamingFileId] = useState(null);
    const [renameValue,    setRenameValue]    = useState('');
    const [pendingDocs,    setPendingDocs]    = useState([]);
    const { appDocs, setAppDocs, reloadAppDocs, loadMoreAppDocs, appDocsHasMore, appDocsLoading, appDocsTotal } =
        useAppDocs(card?.form_id, docsVisible);
    const [modalUploading, setModalUploading] = useState(false);
    const [pendingUploadFiles,   setPendingUploadFiles]   = useState([]);
    const [uploadPickerVisible,  setUploadPickerVisible]  = useState(false);
    const [uploadMode,           setUploadMode]           = useState('choose'); // 'choose' | 'heading-pick' | 'new-heading'
    const [newHeadingName,       setNewHeadingName]       = useState('');
    const [newHeadingLoading,    setNewHeadingLoading]    = useState(false);
    const fileInputRefs = useRef({});

    // Add-item state
    const [addingToHeadingId, setAddingToHeadingId] = useState(null);
    const [newItemValue, setNewItemValue]            = useState('');
    const [addingLoading, setAddingLoading]          = useState(false);
    const [showHeadingPicker, setShowHeadingPicker]  = useState(false);

    const fmtFileSize = (bytes) => {
        if (!bytes) return '—';
        if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1).replace(/\.0$/, '')} MB`;
        if (bytes >= 1e3) return `${Math.round(bytes / 1e3)} KB`;
        return `${bytes} B`;
    };

    const handleDownloadAllAsZip = async () => {
        if (allFiles.length === 0) return;
        setZipLoading(true);
        try {
            const res = await axios.post(downloadFilesZip, { files: allFiles.map(f => f.id) }, { responseType: 'blob' });
            const url  = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${(card.full_name || 'documents').replace(/[^a-zA-Z0-9]/g, '_')}_files.zip`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Download Failed', detail: 'Failed to download ZIP', life: 3000 });
        } finally {
            setZipLoading(false);
        }
    };

    const renameStagedFile = (itemId, idx, newName) =>
        setStagedFiles(prev => ({
            ...prev,
            [itemId]: (prev[itemId] || []).map((f, i) => i === idx ? { ...f, editedName: newName } : f)
        }));

    const openSendAll = () => {
        const withFiles = allItems.filter(i => (i.file_url || []).length > 0).map(i => i.id);
        setSendPickedItems(withFiles);
        setSendChannel('Email');
        setSendRecipients([]);
        setSendMessage('');
        setSendMode('all');
    };

    const openSendItem = (item) => {
        setSendItem(item);
        setSendChannel('Email');
        setSendRecipients([]);
        setSendMessage('');
        setSendMode('item');
    };

    const toggleRecipient = (uid) => setSendRecipients(prev =>
        prev.includes(uid) ? prev.filter(id => id !== uid) : [...prev, uid]
    );

    const togglePickedItem = (itemId) => setSendPickedItems(prev =>
        prev.includes(itemId) ? prev.filter(id => id !== itemId) : [...prev, itemId]
    );

    const closeSendDialog = () => { setSendMode(null); setSendItem(null); };

    const confirmSend = async () => {
        setSendLoading(true);
        try {
            const headings = localHeadings.filter(h => h.id);
            await Promise.all(headings.map(h =>
                axios.post(sendChecklistToContact, {
                    checklist_heading_id: h.id,
                    sales_users: sendRecipients,
                    pipeline_id: 1,
                    userId,
                })
            ));
            toast.current?.show({ severity: 'success', summary: 'Sent', detail: 'Checklist sent to contact', life: 3000 });
            closeSendDialog();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to send', life: 3000 });
        } finally {
            setSendLoading(false);
        }
    };

    const allItems  = localHeadings.flatMap(h => h.items || []);
    const doneCount = allItems.filter(i => i.is_completed).length;
    const total     = allItems.length;
    const allFiles  = allItems.flatMap(it => (it.file_url || []).map(f => ({
        ...f, itemLabel: it.item, itemId: it.id, itemDone: it.is_completed
    })));
    const linkedAppDocIds = new Set(allFiles.map(f => f.app_doc_id).filter(Boolean));
    const allFilesForModal = [
        ...allFiles,
        ...pendingDocs.map(d => ({
            id: null, app_doc_id: d.app_doc_id,
            file_url: d.file_url, file_name: d.file_name, file_size: d.file_size,
            uploaded_by: d.uploaded_by, uploaded_at: d.uploaded_at,
            itemLabel: null, itemId: null, itemDone: false, isPending: true,
        })),
        ...appDocs
            .filter(d => !linkedAppDocIds.has(d.app_doc_id))
            .map(d => ({
                id: null, app_doc_id: d.app_doc_id,
                file_url: d.file_url, file_name: d.file_name, file_size: d.file_size,
                uploaded_by: d.uploaded_by, uploaded_at: d.uploaded_at,
                itemLabel: null, itemId: null, itemDone: false, isIndependent: true,
            })),
    ];

    const updateLocalItem = (id, patch) =>
        setLocalHeadings(prev => prev.map(h => ({
            ...h,
            items: (h.items || []).map(it => it.id === id ? { ...it, ...patch } : it)
        })));

    const handleToggle = (item) => {
        const newVal = !item.is_completed;
        updateLocalItem(item.id, { is_completed: newVal });
        const fd = new FormData();
        fd.append('item', item.item || '');
        fd.append('due_date', item.due_date || '');
        fd.append('is_completed', newVal);
        fd.append('mention', JSON.stringify([]));
        fd.append('assigned_user', JSON.stringify([]));
        axios.post(`${updateCheckList}${item.id}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
            .catch(() => {
                updateLocalItem(item.id, { is_completed: item.is_completed });
                toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update item', life: 3000 });
            });
    };

    const toggleDrawer = (itemId) =>
        setOpenDrawerId(prev => {
            if (prev === itemId) {
                setStagedFiles(p => { const n = { ...p }; delete n[itemId]; return n; });
                setRemovedIds(p => { const n = { ...p }; delete n[itemId]; return n; });
                return null;
            }
            return itemId;
        });

    const stageFiles = (itemId, files) =>
        setStagedFiles(prev => ({
            ...prev,
            [itemId]: [...(prev[itemId] || []), ...files.map(f => ({ file: f, editedName: f.name }))]
        }));

    const unstageFile = (itemId, idx) =>
        setStagedFiles(prev => ({ ...prev, [itemId]: (prev[itemId] || []).filter((_, i) => i !== idx) }));

    const removeExisting = (itemId, fileId) =>
        setRemovedIds(prev => ({ ...prev, [itemId]: [...(prev[itemId] || []), fileId] }));

    const saveFiles = async (item) => {
        const newFiles = stagedFiles[item.id] || [];
        const removed  = removedIds[item.id] || [];
        const kept     = (item.file_url || []).filter(f => !removed.includes(f.id));
        setSaving(item.id);
        try {
            // Step 1: upload each new file to /api/app-docs/ and collect the returned IDs
            const createdDocs = await Promise.all(
                newFiles.map(f => {
                    const fdDoc = new FormData();
                    fdDoc.append('app_id', card.form_id);
                    fdDoc.append('file', f.file);
                    fdDoc.append('file_name', f.editedName);
                    return axios.post(createAppDoc, fdDoc, { headers: { 'Content-Type': 'multipart/form-data' } })
                        .then(r => r.data);
                })
            );
            // Step 2: attach the new docs + handle removals on the checklist item
            const fd = new FormData();
            fd.append('item', item.item || '');
            fd.append('due_date', item.due_date || '');
            fd.append('is_completed', item.is_completed || false);
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            fd.append('removed_file_ids', JSON.stringify(removed));
            fd.append('renamed_files', JSON.stringify(kept.map(f => ({ id: f.id, file_name: f.file_name }))));
            if (createdDocs.length > 0) {
                fd.append('app_doc_ids', JSON.stringify(createdDocs.map(d => d.app_doc_id)));
            }
            const res = await axios.post(`${updateCheckList}${item.id}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.status === 200) {
                updateLocalItem(item.id, { file_url: res.data.file_url || [] });
                setStagedFiles(p => { const n = { ...p }; delete n[item.id]; return n; });
                setRemovedIds(p => { const n = { ...p }; delete n[item.id]; return n; });
                setOpenDrawerId(null);
                toast.current?.show({ severity: 'success', summary: 'Files saved', life: 2000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save files', life: 3000 });
        } finally {
            setSaving(null);
        }
    };

    const handleRenameFile = async (file, newName) => {
        setRenamingFileId(null);
        if (!newName.trim() || newName.trim() === file.file_name) return;
        const trimmed = newName.trim();
        setLocalHeadings(prev => prev.map(h => ({
            ...h,
            items: (h.items || []).map(it => ({
                ...it,
                file_url: (it.file_url || []).map(f => f.id === file.id ? { ...f, file_name: trimmed } : f)
            }))
        })));
        try {
            await axios.patch(`${updateAppDoc}${file.app_doc_id}/`, { file_name: trimmed });
            toast.current?.show({ severity: 'success', summary: 'Renamed', life: 2000 });
            return true;
        } catch {
            setLocalHeadings(prev => prev.map(h => ({
                ...h,
                items: (h.items || []).map(it => ({
                    ...it,
                    file_url: (it.file_url || []).map(f => f.id === file.id ? { ...f, file_name: file.file_name } : f)
                }))
            })));
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to rename', life: 3000 });
            return false;
        }
    };

    const handleRenamePreviewFile = async (name) => {
        const previous = previewFile.file_name;
        setPreviewFile(p => p && { ...p, file_name: name });
        if (await handleRenameFile(previewFile, name) === false) {
            setPreviewFile(p => p && { ...p, file_name: previous });
        }
    };

    const handleRefileDoc = async (file, newItemId) => {
        if (newItemId === file.itemId) return;
        const oldItem = allItems.find(it => it.id === file.itemId);
        const newItem = allItems.find(it => it.id === newItemId);
        if (!oldItem || !newItem) return;

        setLocalHeadings(prev => prev.map(h => ({
            ...h,
            items: (h.items || []).map(it => {
                if (it.id === file.itemId) return { ...it, file_url: (it.file_url || []).filter(f => f.id !== file.id) };
                if (it.id === newItemId)   return { ...it, file_url: [...(it.file_url || []), file] };
                return it;
            })
        })));

        try {
            const fdOld = new FormData();
            fdOld.append('item', oldItem.item || '');
            fdOld.append('due_date', oldItem.due_date || '');
            fdOld.append('is_completed', oldItem.is_completed ? 'true' : 'false');
            fdOld.append('mention', JSON.stringify([]));
            fdOld.append('assigned_user', JSON.stringify([]));
            fdOld.append('removed_file_ids', JSON.stringify([file.id]));
            fdOld.append('renamed_files', JSON.stringify([]));
            await axios.post(`${updateCheckList}${oldItem.id}/${userId}`, fdOld, { headers: { 'Content-Type': 'multipart/form-data' } });

            const fdNew = new FormData();
            fdNew.append('item', newItem.item || '');
            fdNew.append('due_date', newItem.due_date || '');
            fdNew.append('is_completed', newItem.is_completed ? 'true' : 'false');
            fdNew.append('mention', JSON.stringify([]));
            fdNew.append('assigned_user', JSON.stringify([]));
            fdNew.append('app_doc_ids', JSON.stringify([file.app_doc_id]));
            fdNew.append('renamed_files', JSON.stringify([]));
            const res = await axios.post(`${updateCheckList}${newItem.id}/${userId}`, fdNew, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data?.file_url) updateLocalItem(newItem.id, { file_url: res.data.file_url });
            toast.current?.show({ severity: 'success', summary: 'Document moved', life: 2000 });
        } catch {
            setLocalHeadings(prev => prev.map(h => ({
                ...h,
                items: (h.items || []).map(it => {
                    if (it.id === file.itemId) return { ...it, file_url: [...(it.file_url || []), file] };
                    if (it.id === newItemId)   return { ...it, file_url: (it.file_url || []).filter(f => f.id !== file.id) };
                    return it;
                })
            })));
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to move document', life: 3000 });
        }
    };

    const doModalUpload = async (files, headingId) => {
        setModalUploading(true);
        try {
            const created = await Promise.all(
                files.map(f => {
                    const fd = new FormData();
                    fd.append('app_id', card.form_id);
                    fd.append('file', f);
                    fd.append('file_name', f.name);
                    return axios.post(createAppDoc, fd, { headers: { 'Content-Type': 'multipart/form-data' } })
                        .then(r => r.data);
                })
            );

            const resolvedHeadingId = headingId || localHeadings[0]?.id;

            if (resolvedHeadingId) {
                const fdCreate = new FormData();
                fdCreate.append('item', '');
                fdCreate.append('due_date', '');
                fdCreate.append('heading_id', resolvedHeadingId);
                fdCreate.append('mention', JSON.stringify([]));
                fdCreate.append('assigned_user', JSON.stringify([]));
                const createRes = await axios.post(
                    `${createCheckList}${card.form_id}/${userId}`,
                    fdCreate,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );
                const newItemId = createRes.data.checklist_id;

                const fdAssign = new FormData();
                fdAssign.append('item', '');
                fdAssign.append('due_date', '');
                fdAssign.append('is_completed', 'false');
                fdAssign.append('mention', JSON.stringify([]));
                fdAssign.append('assigned_user', JSON.stringify([]));
                fdAssign.append('app_doc_ids', JSON.stringify(created.map(d => d.app_doc_id)));
                fdAssign.append('renamed_files', JSON.stringify([]));
                const assignRes = await axios.post(
                    `${updateCheckList}${newItemId}/${userId}`,
                    fdAssign,
                    { headers: { 'Content-Type': 'multipart/form-data' } }
                );

                const newItem = {
                    id: newItemId,
                    item: '',
                    is_completed: false,
                    due_date: '',
                    file_url: assignRes.data?.file_url || created,
                    mention: [],
                    assigned_user: [],
                };
                setLocalHeadings(prev => prev.map(h =>
                    h.id === resolvedHeadingId ? { ...h, items: [...(h.items || []), newItem] } : h
                ));
            } else {
                setPendingDocs(prev => [...prev, ...created]);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Upload failed', detail: 'Could not upload file(s)', life: 3000 });
        } finally {
            setModalUploading(false);
        }
    };

    const doIndependentUpload = async (files) => {
        setModalUploading(true);
        try {
            await Promise.all(
                files.map(f => {
                    const fd = new FormData();
                    fd.append('app_id', card.form_id);
                    fd.append('file', f);
                    fd.append('file_name', f.name);
                    return axios.post(createAppDoc, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                })
            );
            await reloadAppDocs();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Upload failed', detail: 'Could not upload file(s)', life: 3000 });
        } finally {
            setModalUploading(false);
        }
    };

    const handleModalUpload = (files) => {
        setPendingUploadFiles(files);
        setUploadMode('choose');
        setUploadPickerVisible(true);
    };

    const handleUploadHeadingPick = (headingId) => {
        const files = pendingUploadFiles;
        setPendingUploadFiles([]);
        setUploadPickerVisible(false);
        setUploadMode('choose');
        doModalUpload(files, headingId);
    };

    const handleUploadIndependent = () => {
        const files = pendingUploadFiles;
        setPendingUploadFiles([]);
        setUploadPickerVisible(false);
        setUploadMode('choose');
        doIndependentUpload(files);
    };

    const handleCreateHeadingAndUpload = async () => {
        if (!newHeadingName.trim()) return;
        setNewHeadingLoading(true);
        try {
            const res = await axios.post(`${createCheckListHeading}${card.form_id}`, { name: newHeadingName.trim() });
            if (res.status === 200) {
                const newHeading = { id: res.data.id, name: newHeadingName.trim(), items: [] };
                setLocalHeadings(prev => [...prev, newHeading]);
                const files = pendingUploadFiles;
                setPendingUploadFiles([]);
                setUploadPickerVisible(false);
                setUploadMode('choose');
                setNewHeadingName('');
                doModalUpload(files, res.data.id);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Could not create heading', life: 3000 });
        } finally {
            setNewHeadingLoading(false);
        }
    };

    const cancelUploadPicker = () => {
        setPendingUploadFiles([]);
        setUploadPickerVisible(false);
        setUploadMode('choose');
        setNewHeadingName('');
    };

    const handleUpdateItemName = async (item, newName) => {
        if (newName === item.item) { setEditingItemId(null); return; }
        const prevName = item.item;
        updateLocalItem(item.id, { item: newName });
        setEditingItemId(null);
        try {
            const fd = new FormData();
            fd.append('item', newName);
            fd.append('due_date', item.due_date || '');
            fd.append('is_completed', item.is_completed ? 'true' : 'false');
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            fd.append('renamed_files', JSON.stringify([]));
            await axios.post(`${updateCheckList}${item.id}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch {
            updateLocalItem(item.id, { item: prevName });
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update item name', life: 3000 });
        }
    };

    const handleUpdateDueDate = async (item, newDate) => {
        const prev = item.due_date;
        updateLocalItem(item.id, { due_date: newDate });
        try {
            const fd = new FormData();
            fd.append('item', item.item || '');
            fd.append('due_date', newDate);
            fd.append('is_completed', item.is_completed ? 'true' : 'false');
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            fd.append('renamed_files', JSON.stringify([]));
            await axios.post(`${updateCheckList}${item.id}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch {
            updateLocalItem(item.id, { due_date: prev });
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update due date', life: 3000 });
        }
    };

    const handleSaveNote = async (item) => {
        if (!noteText.trim()) { setNoteItemId(null); setNoteText(''); return; }
        setSavingNote(true);
        const prevNote = item.notes;
        updateLocalItem(item.id, { notes: noteText.trim() });
        try {
            await axios.post(`${updateCheckList}${item.id}/${userId}`, {
                item: item.item || '',
                notes: noteText.trim(),
                due_date: item.due_date || '',
                is_completed: item.is_completed || false,
                mention: [],
                assigned_user: [],
                app_doc_ids: [],
                removed_file_ids: [],
            });
            setNoteItemId(null);
            setNoteText('');
            toast.current?.show({ severity: 'success', summary: 'Note saved', life: 2000 });
        } catch {
            updateLocalItem(item.id, { notes: prevNote });
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save note', life: 3000 });
        } finally {
            setSavingNote(false);
        }
    };

    const handleAssignIndependentToHeading = async (doc, headingId) => {
        setAppDocs(prev => prev.filter(d => d.app_doc_id !== doc.app_doc_id));
        try {
            const fdCreate = new FormData();
            fdCreate.append('item', '');
            fdCreate.append('due_date', '');
            fdCreate.append('heading_id', headingId);
            fdCreate.append('mention', JSON.stringify([]));
            fdCreate.append('assigned_user', JSON.stringify([]));
            const createRes = await axios.post(`${createCheckList}${card.form_id}/${userId}`, fdCreate, { headers: { 'Content-Type': 'multipart/form-data' } });
            const newItemId = createRes.data.checklist_id;

            const fdAssign = new FormData();
            fdAssign.append('item', '');
            fdAssign.append('due_date', '');
            fdAssign.append('is_completed', 'false');
            fdAssign.append('mention', JSON.stringify([]));
            fdAssign.append('assigned_user', JSON.stringify([]));
            fdAssign.append('app_doc_ids', JSON.stringify([doc.app_doc_id]));
            fdAssign.append('renamed_files', JSON.stringify([]));
            const assignRes = await axios.post(`${updateCheckList}${newItemId}/${userId}`, fdAssign, { headers: { 'Content-Type': 'multipart/form-data' } });

            const newItem = {
                id: newItemId, item: '', is_completed: false, due_date: '',
                file_url: assignRes.data?.file_url || [doc],
                mention: [], assigned_user: [],
            };
            setLocalHeadings(prev => prev.map(h =>
                h.id === headingId ? { ...h, items: [...(h.items || []), newItem] } : h
            ));
            toast.current?.show({ severity: 'success', summary: 'Document filed', life: 2000 });
        } catch {
            setAppDocs(prev => [...prev, doc]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to file document', life: 3000 });
        }
    };

    const handleAssignPendingDoc = async (doc, itemId) => {
        const item = allItems.find(it => it.id === itemId);
        if (!item) return;
        setPendingDocs(prev => prev.filter(d => d.app_doc_id !== doc.app_doc_id));
        try {
            const fd = new FormData();
            fd.append('item', item.item || '');
            fd.append('due_date', item.due_date || '');
            fd.append('is_completed', item.is_completed ? 'true' : 'false');
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            fd.append('app_doc_ids', JSON.stringify([doc.app_doc_id]));
            fd.append('renamed_files', JSON.stringify([]));
            const res = await axios.post(`${updateCheckList}${item.id}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            if (res.data?.file_url) updateLocalItem(item.id, { file_url: res.data.file_url });
            toast.current?.show({ severity: 'success', summary: 'Document filed', life: 2000 });
        } catch {
            setPendingDocs(prev => [...prev, doc]);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to file document', life: 3000 });
        }
    };

    const handleDeleteDoc = async (f) => {
        if (!f.app_doc_id) return;
        try {
            if (!f.isPending && !f.isIndependent && f.itemId) {
                const fd = new FormData();
                fd.append('item', f.itemLabel || '');
                fd.append('due_date', '');
                fd.append('is_completed', 'false');
                fd.append('mention', JSON.stringify([]));
                fd.append('assigned_user', JSON.stringify([]));
                fd.append('removed_file_ids', JSON.stringify([f.id]));
                fd.append('renamed_files', JSON.stringify([]));
                await axios.post(`${updateCheckList}${f.itemId}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                setLocalHeadings(prev => prev.map(h => ({
                    ...h,
                    items: (h.items || []).map(it => ({
                        ...it,
                        file_url: (it.file_url || []).filter(file => file.app_doc_id !== f.app_doc_id),
                    })),
                })));
            } else {
                await axios.delete(`${updateAppDoc}${f.app_doc_id}/`);
                if (f.isPending) {
                    setPendingDocs(prev => prev.filter(d => d.app_doc_id !== f.app_doc_id));
                } else {
                    setAppDocs(prev => prev.filter(d => d.app_doc_id !== f.app_doc_id));
                }
            }
            toast.current?.show({ severity: 'success', summary: 'Deleted', life: 2000 });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete document', life: 3000 });
        }
    };

    const handleRenameToSuggestion = async (doc, newName) => {
        const item = allItems.find(it => it.id === doc.itemId);
        if (!item) return;
        const prevName = item.item;
        updateLocalItem(item.id, { item: newName });
        try {
            const fd = new FormData();
            fd.append('item', newName);
            fd.append('due_date', item.due_date || '');
            fd.append('is_completed', item.is_completed ? 'true' : 'false');
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            fd.append('renamed_files', JSON.stringify([]));
            await axios.post(`${updateCheckList}${item.id}/${userId}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            toast.current?.show({ severity: 'success', summary: 'Checklist item updated', life: 2000 });
        } catch {
            updateLocalItem(item.id, { item: prevName });
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update item', life: 3000 });
        }
    };

    const handleDragOver  = e => { e.preventDefault(); e.currentTarget.classList.add('drag'); };
    const handleDragLeave = e => e.currentTarget.classList.remove('drag');
    const handleDrop      = (e, itemId) => {
        e.preventDefault();
        e.currentTarget.classList.remove('drag');
        const files = [...e.dataTransfer.files];
        if (files.length) stageFiles(itemId, files);
    };

    const handleAddClick = () => {
        if (localHeadings.length === 0) return;
        setNewItemValue('');
        if (localHeadings.length === 1) {
            setAddingToHeadingId(localHeadings[0].id);
            setShowHeadingPicker(false);
        } else {
            setShowHeadingPicker(prev => !prev);
            setAddingToHeadingId(null);
        }
    };

    const handlePickHeading = (headingId) => {
        setAddingToHeadingId(headingId);
        setShowHeadingPicker(false);
        setNewItemValue('');
    };

    const cancelAdd = () => {
        setAddingToHeadingId(null);
        setNewItemValue('');
        setShowHeadingPicker(false);
    };

    const handleSaveNewItem = async () => {
        if (!newItemValue.trim() || !addingToHeadingId) return;
        setAddingLoading(true);
        try {
            const fd = new FormData();
            fd.append('item', newItemValue.trim());
            fd.append('due_date', '');
            fd.append('heading_id', addingToHeadingId);
            fd.append('mention', JSON.stringify([]));
            fd.append('assigned_user', JSON.stringify([]));
            const res = await axios.post(`${createCheckList}${card.form_id}/${userId}`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            if (res.status === 200) {
                const newItem = {
                    id: res.data.checklist_id,
                    item: newItemValue.trim(),
                    is_completed: false,
                    file_url: [],
                    due_date: '',
                    notes: '',
                };
                setLocalHeadings(prev => prev.map(h =>
                    h.id === addingToHeadingId
                        ? { ...h, items: [...(h.items || []), newItem] }
                        : h
                ));
                setNewItemValue('');
                setAddingToHeadingId(null);
                toast.current?.show({ severity: 'success', summary: 'Item added', life: 2000 });
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to add item', life: 3000 });
        } finally {
            setAddingLoading(false);
        }
    };

    return (
        <div className={`pcq-col-checklist${light ? ' light' : ''}`}>
            {/* Header */}
            <div className="pcq-cl-head">
                <h3>Next Steps &amp; Documents</h3>
                <div className="pcq-cl-head-actions">
                    <button className="pcq-icon-btn" onClick={() => setDocsVisible(true)}>📂 Documents</button>
                    <button
                        className="pcq-icon-btn"
                        onClick={handleDownloadAllAsZip}
                        disabled={allFiles.length === 0 || zipLoading}
                    >
                        {zipLoading ? '⏳ Zipping…' : '⬇ Download Zip'}
                    </button>
                    {/* <button className="pcq-icon-btn" onClick={openSendAll}>
                        📤 Send
                    </button>
                    <button
                        className="pcq-icon-btn accent"
                        onClick={onPing}
                        disabled={pinging || card.outstanding === 0}
                        title={card.outstanding === 0 ? 'No outstanding items' : 'Send email + SMS reminder to client'}
                    >
                        {pinging ? '⏳ Sending…' : '🔔 Send Reminder'}
                    </button> */}
                </div>
            </div>

            {/* Progress */}
            <div className="pcq-cl-sub">
                <div className="pcq-progress-steps">
                    {allItems.map((it, i) => (
                        <span key={i} className={`pcq-step${it.is_completed ? ' done' : ''}`} />
                    ))}
                    {allItems.length === 0 && <span className="pcq-step" />}
                </div>
                <span className="pcq-progress-txt">{doneCount}/{total}</span>
                {allFiles.length > 0 && (
                    <span className="pcq-docs-count">{allFiles.length} file{allFiles.length !== 1 ? 's' : ''}</span>
                )}
            </div>

            {/* Checklist items */}
            <div className="pcq-checklist">
                {localHeadings.map(heading => {
                    const visibleItems = (heading.items || []).filter(item => showCompleted || !item.is_completed);
                    const isAddingHere = addingToHeadingId === heading.id;
                    if (visibleItems.length === 0 && !isAddingHere) return null;
                    return (
                        <React.Fragment key={heading.id}>
                            {visibleItems.map(item => {
                                const isOpen       = openDrawerId === item.id;
                                const staged       = stagedFiles[item.id] || [];
                                const removed      = removedIds[item.id] || [];
                                const existing     = (item.file_url || []).filter(f => !removed.includes(f.id));
                                const hasChanges   = staged.length > 0 || removed.length > 0;
                                const isSavingThis = saving === item.id;

                                return (
                                    <div key={item.id} className={`pcq-check-item${item.is_completed ? ' checked' : ''}${isOpen ? ' drawer-open' : ''}`}>
                                        {/* Main row — click to open drawer */}
                                        <div className="pcq-ci-main" onClick={() => toggleDrawer(item.id)}>
                                            <div
                                                className="pcq-checkbox"
                                                onClick={e => { e.stopPropagation(); handleToggle(item); }}
                                            />
                                            {editingItemId === item.id ? (
                                                <input
                                                    className="pcq-check-label-edit"
                                                    autoFocus
                                                    value={editingItemValue}
                                                    onChange={e => setEditingItemValue(e.target.value)}
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter') handleUpdateItemName(item, editingItemValue);
                                                        if (e.key === 'Escape') setEditingItemId(null);
                                                    }}
                                                    onBlur={() => handleUpdateItemName(item, editingItemValue)}
                                                    onClick={e => e.stopPropagation()}
                                                />
                                            ) : (
                                                <span
                                                    className="pcq-check-label"
                                                    onDoubleClick={e => {
                                                        e.stopPropagation();
                                                        setEditingItemId(item.id);
                                                        setEditingItemValue(item.item || '');
                                                    }}
                                                    title="Double-click to edit"
                                                >{item.item || '(unnamed)'}</span>
                                            )}
                                            <div className="pcq-ci-badges">
                                                {(item.file_url || []).length > 0 && (
                                                    <span className="pcq-attach-pill">📎 {item.file_url.length}</span>
                                                )}
                                                {(() => { const d = dueInfo(item.due_date); return d ? <span className={`pcq-due-badge ${d.cls}`}>{d.txt}</span> : null; })()}
                                            </div>
                                        </div>

                                        {/* Note preview with edit icon (collapsed, note exists, not editing) */}
                                        {!isOpen && item.notes && noteItemId !== item.id && (
                                            <div className="pcq-item-note-preview" onClick={e => e.stopPropagation()}>
                                                <span className="pcq-item-note-preview-icon">📝</span>
                                                <span className="pcq-item-note-preview-text">{item.notes}</span>
                                                <button className="pcq-note-edit-icon" onClick={e => { e.stopPropagation(); setNoteItemId(item.id); setNoteText(item.notes || ''); }} title="Edit note">✏️</button>
                                            </div>
                                        )}

                                        {/* File chips (collapsed view) */}
                                        {!isOpen && existing.length > 0 && (
                                            <div className="pcq-item-files">
                                                {existing.map((f, i) => (
                                                    <button key={i} className="pcq-file-chip"
                                                        onClick={e => { e.stopPropagation(); setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }}>
                                                        📄 <span>{f.file_name}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        )}

                                        {/* Per-item tools — right-aligned, below docs */}
                                        {!isOpen && (
                                            <div className="pcq-ci-tools">
                                                {!item.notes && noteItemId !== item.id && (
                                                    <button className="pcq-ci-tool" onClick={e => { e.stopPropagation(); setNoteItemId(item.id); setNoteText(''); }}>
                                                        📝 Add note
                                                    </button>
                                                )}
                                                <div className="pcq-ci-date-wrap" title={item.due_date ? `Due: ${item.due_date}` : 'Set due date'} onClick={e => e.stopPropagation()}>
                                                    <span className="pcq-ci-date-icon">📅</span>
                                                    {item.due_date && (
                                                        <span className="pcq-ci-date-val">{item.due_date.slice(5)}</span>
                                                    )}
                                                    <input
                                                        type="date"
                                                        className="pcq-ci-date-hidden"
                                                        value={item.due_date || ''}
                                                        onChange={e => { e.stopPropagation(); handleUpdateDueDate(item, e.target.value); }}
                                                        onClick={e => e.stopPropagation()}
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        {/* Inline note editor (collapsed — only note section, no drawer) */}
                                        {!isOpen && noteItemId === item.id && (
                                            <div className="pcq-note-box pcq-note-box--inline" onClick={e => e.stopPropagation()}>
                                                <textarea
                                                    className="pcq-note-input"
                                                    placeholder="Note about this item…"
                                                    value={noteText}
                                                    autoFocus
                                                    onChange={e => setNoteText(e.target.value)}
                                                />
                                                <div className="pcq-note-actions">
                                                    <button className="pcq-edit-save" onClick={() => handleSaveNote(item)} disabled={savingNote}>
                                                        {savingNote ? 'Saving…' : 'Save note'}
                                                    </button>
                                                    <button className="pcq-edit-cancel" onClick={() => { setNoteItemId(null); setNoteText(''); }}>Cancel</button>
                                                </div>
                                            </div>
                                        )}

                                        {/* Upload drawer */}
                                        {isOpen && (
                                            <div className="pcq-upload-drawer">
                                                <input
                                                    type="file"
                                                    multiple
                                                    ref={el => { fileInputRefs.current[item.id] = el; }}
                                                    style={{ display: 'none' }}
                                                    onChange={e => { stageFiles(item.id, [...e.target.files]); e.target.value = ''; }}
                                                />
                                                <div
                                                    className="pcq-dropzone"
                                                    onDragOver={handleDragOver}
                                                    onDragLeave={handleDragLeave}
                                                    onDrop={e => handleDrop(e, item.id)}
                                                    onClick={() => fileInputRefs.current[item.id]?.click()}
                                                >
                                                    ⬆ <b>Drag &amp; drop</b> or click to upload
                                                </div>

                                                {(existing.length > 0 || staged.length > 0) && (
                                                    <div className="pcq-file-list">
                                                        {existing.map((f, i) => (
                                                            <div key={i} className="pcq-file-row">
                                                                <span className="pcq-file-name"
                                                                    onClick={() => { setPreviewFile({ ...f, file_url: resolveFileUrl(f.file_url) }); setPreviewVisible(true); }}>
                                                                    📄 {f.file_name}
                                                                </span>
                                                                <button className="pcq-file-remove" onClick={() => removeExisting(item.id, f.id)}>✕</button>
                                                            </div>
                                                        ))}
                                                        {staged.map((f, i) => (
                                                            <div key={i} className="pcq-file-row pcq-file-new">
                                                                <span className="pcq-file-icon">📄</span>
                                                                <input
                                                                    className="pcq-file-name-input"
                                                                    value={f.editedName}
                                                                    onChange={e => renameStagedFile(item.id, i, e.target.value)}
                                                                    onClick={e => e.stopPropagation()}
                                                                />
                                                                <button className="pcq-file-remove" onClick={() => unstageFile(item.id, i)}>✕</button>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}

                                                {/* Note display + editor — inside drawer */}
                                                <div className="pcq-note-section">
                                                    {item.notes && noteItemId !== item.id && (
                                                        <div className="pcq-note-display">
                                                            <span className="pcq-note-display-label">📝 Note</span>
                                                            <p className="pcq-note-display-text">{item.notes}</p>
                                                        </div>
                                                    )}
                                                    {noteItemId === item.id ? (
                                                        <div className="pcq-note-box">
                                                            <textarea
                                                                className="pcq-note-input"
                                                                placeholder="Note about this item — posts to the activity log, attached to this item…"
                                                                value={noteText}
                                                                autoFocus
                                                                onChange={e => setNoteText(e.target.value)}
                                                            />
                                                            <div className="pcq-note-actions">
                                                                <button className="pcq-edit-save" onClick={() => handleSaveNote(item)} disabled={savingNote}>
                                                                    {savingNote ? 'Saving…' : 'Save note'}
                                                                </button>
                                                                <button className="pcq-edit-cancel" onClick={() => { setNoteItemId(null); setNoteText(''); }}>Cancel</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <button className="pcq-add-note-btn" onClick={e => { e.stopPropagation(); setNoteItemId(item.id); setNoteText(item.notes || ''); }}>
                                                            📝 {item.notes ? 'Edit note' : 'Add note'}
                                                        </button>
                                                    )}
                                                </div>

                                                <div className="pcq-drawer-actions">
                                                    {hasChanges && (
                                                        <button className="pcq-edit-save" onClick={() => saveFiles(item)} disabled={isSavingThis}>
                                                            {isSavingThis ? 'Saving…' : 'Save'}
                                                        </button>
                                                    )}
                                                    <button className="pcq-drawer-mark-btn" onClick={e => { e.stopPropagation(); handleToggle(item); }}>
                                                        {item.is_completed ? 'Mark incomplete' : '✓ Mark complete'}
                                                    </button>
                                                    <button className="pcq-edit-cancel" onClick={() => toggleDrawer(item.id)}>Close</button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {isAddingHere && (
                                <div className="pcq-add-item-form">
                                    <input
                                        className="pcq-add-item-input"
                                        autoFocus
                                        placeholder="Item name…"
                                        value={newItemValue}
                                        onChange={e => setNewItemValue(e.target.value)}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter') handleSaveNewItem();
                                            if (e.key === 'Escape') cancelAdd();
                                        }}
                                    />
                                    <div className="pcq-add-item-actions">
                                        <button className="pcq-edit-save" onClick={handleSaveNewItem} disabled={!newItemValue.trim() || addingLoading}>
                                            {addingLoading ? 'Adding…' : 'Add item'}
                                        </button>
                                        <button className="pcq-edit-cancel" onClick={cancelAdd}>Cancel</button>
                                    </div>
                                </div>
                            )}
                        </React.Fragment>
                    );
                })}
                {allItems.length === 0 && <p className="pcq-cl-empty">No checklist items.</p>}
            </div>

            {/* Add item */}
            <div className="pcq-add-item-bar">
                <button className="pcq-icon-btn" onClick={handleAddClick}>+ Add item</button>
                {showHeadingPicker && (
                    <div className="pcq-heading-picker">
                        <div className="pcq-heading-picker-label">Select a section:</div>
                        {localHeadings.map(h => (
                            <button key={h.id} className="pcq-heading-option" onClick={() => handlePickHeading(h.id)}>
                                {h.name}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {doneCount > 0 && (
                <button className="pcq-show-completed-btn" onClick={() => setShowCompleted(s => !s)}>
                    {showCompleted ? `↑ Hide ${doneCount} completed` : `↓ Show ${doneCount} completed`}
                </button>
            )}

            {/* View All Docs modal */}
            <DocsModal
                visible={docsVisible}
                onHide={() => { setDocsVisible(false); setRenamingFileId(null); }}
                card={card}
                allFilesForModal={allFilesForModal}
                allItems={allItems}
                fmtFileSize={fmtFileSize}
                renamingFileId={renamingFileId}
                setRenamingFileId={setRenamingFileId}
                renameValue={renameValue}
                setRenameValue={setRenameValue}
                handleRenameFile={handleRenameFile}
                handleRefileDoc={handleRefileDoc}
                handleAssignPendingDoc={handleAssignPendingDoc}
                handleAssignIndependentToHeading={handleAssignIndependentToHeading}
                localHeadings={localHeadings}
                handleToggle={handleToggle}
                setPreviewFile={setPreviewFile}
                setPreviewVisible={setPreviewVisible}
                docViewMode={docViewMode}
                setDocViewMode={setDocViewMode}
                handleModalUpload={handleModalUpload}
                handleDragDropUpload={doIndependentUpload}
                modalUploading={modalUploading}
                handleRenameToSuggestion={handleRenameToSuggestion}
                handleDeleteDoc={handleDeleteDoc}
                hasMoreDocs={appDocsHasMore}
                loadingMoreDocs={appDocsLoading}
                onLoadMoreDocs={loadMoreAppDocs}
                docsLoaded={appDocs.length}
                docsTotal={appDocsTotal}
            />

            {/* File preview */}
            {previewVisible && previewFile && createPortal(
                <div className="pcq-preview-backdrop" onClick={() => { setPreviewVisible(false); setPreviewFile(null); }}>
                    <div className="pcq-preview-modal" onClick={e => e.stopPropagation()}>
                        <div className="pcq-preview-hd">
                            <PreviewTitle file={previewFile} onRename={handleRenamePreviewFile} />
                            <div className="pcq-preview-hd-actions">
                                <a className="pcq-preview-dl" href={previewFile.file_url} download={previewFile.file_name} target="_blank" rel="noreferrer">⬇ Download</a>
                                <button className="pcq-preview-close" onClick={() => { setPreviewVisible(false); setPreviewFile(null); }}>✕</button>
                            </div>
                        </div>
                        <div className="pcq-preview-body">
                            <PreviewFile isSingleFile={true} setPreviewVisible={setPreviewVisible} previewFile={previewFile} />
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* ── Send All Items dialog ──────────────────── */}
            <AppModal
                visible={sendMode === 'all'}
                onHide={closeSendDialog}
                header="Send items"
                panelStyle={{ width: '480px', maxWidth: '95vw' }}
            >
                <div className="pcq-send-body">
                    <p className="pcq-send-sub">Pick which items to attach for {card.full_name}</p>

                    <div className="pcq-send-section">
                        <label className="pcq-send-label">
                            ITEMS ({sendPickedItems.length} SELECTED · {
                                allItems.filter(i => sendPickedItems.includes(i.id))
                                    .reduce((n, i) => n + (i.file_url || []).length, 0)
                            } FILES)
                        </label>
                        <div className="pcq-send-items-list">
                            {allItems.map(item => {
                                const picked = sendPickedItems.includes(item.id);
                                const fc = (item.file_url || []).length;
                                return (
                                    <div
                                        key={item.id}
                                        className={`pcq-send-item-row${picked ? ' selected' : ''}`}
                                        onClick={() => togglePickedItem(item.id)}
                                    >
                                        <div className={`pcq-send-cb${picked ? ' checked' : ''}`}>
                                            {picked && '✓'}
                                        </div>
                                        <span className="pcq-send-item-label">{item.item}</span>
                                        <span className="pcq-send-item-fc">{fc ? `📎 ${fc}` : 'no files'}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="pcq-send-section">
                        <label className="pcq-send-label">CHANNEL</label>
                        <div className="pcq-send-channel">
                            <button className={`pcq-send-ch${sendChannel === 'Email' ? ' active' : ''}`} onClick={() => setSendChannel('Email')}>Email</button>
                            <button className={`pcq-send-ch${sendChannel === 'SMS' ? ' active' : ''}`} onClick={() => setSendChannel('SMS')}>SMS</button>
                        </div>
                    </div>

                    <div className="pcq-send-section">
                        <label className="pcq-send-label">SEND TO</label>
                        <div className="pcq-send-recipients">
                            {usersList.map(u => {
                                const sel = sendRecipients.includes(u.value);
                                return (
                                    <div key={u.value} className={`pcq-send-recipient${sel ? ' selected' : ''}`} onClick={() => toggleRecipient(u.value)}>
                                        <div className="pcq-send-av">
                                            {u.image ? <img src={u.image} alt="" className="pcq-send-av-img" /> : getInitials(u.label)}
                                        </div>
                                        <span className="pcq-send-rname">{u.label}</span>
                                        {u.role && <span className="pcq-send-rrole">{u.role}</span>}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    <div className="pcq-send-section">
                        <label className="pcq-send-label">MESSAGE</label>
                        <textarea className="pcq-send-msg" placeholder="Write a message to go with these…" value={sendMessage} onChange={e => setSendMessage(e.target.value)} />
                    </div>

                    <div className="pcq-send-footer">
                        <button className="pcq-send-cancel" onClick={closeSendDialog}>Cancel</button>
                        <button
                            className="pcq-send-confirm"
                            disabled={sendPickedItems.length === 0 || sendRecipients.length === 0 || sendLoading}
                            onClick={confirmSend}
                        >
                            {sendLoading ? 'Sending…' : `Send ${sendPickedItems.length} item${sendPickedItems.length !== 1 ? 's' : ''}`}
                        </button>
                    </div>
                </div>
            </AppModal>

            {/* ── Send Single Item dialog ────────────────── */}
            <AppModal
                visible={sendMode === 'item'}
                onHide={closeSendDialog}
                header="Send document / step"
                panelStyle={{ width: '480px', maxWidth: '95vw' }}
            >
                {sendItem && (
                    <div className="pcq-send-body">
                        <p className="pcq-send-sub">"{sendItem.item}"</p>

                        <div className="pcq-send-section">
                            {(sendItem.file_url || []).length === 0 ? (
                                <div className="pcq-send-nofiles">
                                    No files on this item yet — this will send a link to the item.
                                </div>
                            ) : (
                                <div className="pcq-send-files-preview">
                                    {sendItem.file_url.map((f, i) => (
                                        <div key={i} className="pcq-send-file">📄 {f.file_name}</div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="pcq-send-section">
                            <label className="pcq-send-label">CHANNEL</label>
                            <div className="pcq-send-channel">
                                <button className={`pcq-send-ch${sendChannel === 'Email' ? ' active' : ''}`} onClick={() => setSendChannel('Email')}>Email</button>
                                <button className={`pcq-send-ch${sendChannel === 'SMS' ? ' active' : ''}`} onClick={() => setSendChannel('SMS')}>SMS</button>
                            </div>
                        </div>

                        <div className="pcq-send-section">
                            <label className="pcq-send-label">SEND TO</label>
                            <div className="pcq-send-recipients">
                                {usersList.map(u => {
                                    const sel = sendRecipients.includes(u.value);
                                    return (
                                        <div key={u.value} className={`pcq-send-recipient${sel ? ' selected' : ''}`} onClick={() => toggleRecipient(u.value)}>
                                            <div className="pcq-send-av">
                                                {u.image ? <img src={u.image} alt="" className="pcq-send-av-img" /> : getInitials(u.label)}
                                            </div>
                                            <span className="pcq-send-rname">{u.label}</span>
                                            {u.role && <span className="pcq-send-rrole">{u.role}</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="pcq-send-section">
                            <label className="pcq-send-label">MESSAGE</label>
                            <textarea className="pcq-send-msg" placeholder="Write a message to go with this…" value={sendMessage} onChange={e => setSendMessage(e.target.value)} />
                        </div>

                        <div className="pcq-send-footer">
                            <button className="pcq-send-cancel" onClick={closeSendDialog}>Cancel</button>
                            <button
                                className="pcq-send-confirm"
                                disabled={sendRecipients.length === 0 || sendLoading}
                                onClick={confirmSend}
                            >
                                {sendLoading ? 'Sending…' : 'Send to selected'}
                            </button>
                        </div>
                    </div>
                )}
            </AppModal>

            {/* ── Upload intent picker ────────────────── */}
            {uploadPickerVisible && (
                <div className="pcq-upload-picker-backdrop" onClick={cancelUploadPicker}>
                    <div className="pcq-upload-picker" onClick={e => e.stopPropagation()}>

                        {/* Step 1: choose checklist vs independent */}
                        {uploadMode === 'choose' && (
                            <>
                                <div className="pcq-upload-picker-title">
                                    How would you like to add {pendingUploadFiles.length === 1 ? 'this file' : `these ${pendingUploadFiles.length} files`}?
                                </div>
                                {localHeadings.length === 0 ? (
                                    <button className="pcq-upload-picker-option" onClick={() => setUploadMode('new-heading')}>
                                        📋 Add to checklist — create a heading first
                                    </button>
                                ) : localHeadings.length === 1 ? (
                                    <button className="pcq-upload-picker-option" onClick={() => handleUploadHeadingPick(localHeadings[0].id)}>
                                        📋 Add to checklist — "{localHeadings[0].name}"
                                    </button>
                                ) : (
                                    <button className="pcq-upload-picker-option" onClick={() => setUploadMode('heading-pick')}>
                                        📋 Add to checklist
                                    </button>
                                )}
                                <button className="pcq-upload-picker-option" onClick={handleUploadIndependent}>
                                    📄 Upload as independent document
                                </button>
                                <button className="pcq-upload-picker-cancel" onClick={cancelUploadPicker}>Cancel</button>
                            </>
                        )}

                        {/* Step 2a: pick which heading */}
                        {uploadMode === 'heading-pick' && (
                            <>
                                <div className="pcq-upload-picker-title">Choose a section:</div>
                                {localHeadings.map(h => (
                                    <button key={h.id} className="pcq-upload-picker-option" onClick={() => handleUploadHeadingPick(h.id)}>
                                        {h.name}
                                    </button>
                                ))}
                                <button className="pcq-upload-picker-cancel" onClick={() => setUploadMode('choose')}>← Back</button>
                            </>
                        )}

                        {/* Step 2b: create a new heading */}
                        {uploadMode === 'new-heading' && (
                            <>
                                <div className="pcq-upload-picker-title">Create a checklist heading:</div>
                                <input
                                    className="pcq-upload-heading-input"
                                    placeholder="Heading name…"
                                    autoFocus
                                    value={newHeadingName}
                                    onChange={e => setNewHeadingName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') handleCreateHeadingAndUpload(); }}
                                />
                                <button
                                    className="pcq-upload-picker-option"
                                    disabled={!newHeadingName.trim() || newHeadingLoading}
                                    onClick={handleCreateHeadingAndUpload}
                                >
                                    {newHeadingLoading ? 'Creating…' : 'Create & upload'}
                                </button>
                                <button className="pcq-upload-picker-cancel" onClick={() => setUploadMode('choose')}>← Back</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Activity Column ────────────────────────────────────────
function ActivityColumn({ activity, contacts, formId, userId, toast, usersList }) {
    const user        = useSelector(s => s.user);
    const isSuperuser = useSelector(s => s.user.isSuperuser);
    const mentionRef     = useRef(null);
    const editMentionRef = useRef(null);

    const [feed,          setFeed]          = useState(activity);
    const [value,         setValue]         = useState('');
    const [editValue,     setEditValue]     = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [editUsers,     setEditUsers]     = useState([]);
    const [suggestions,   setSuggestions]   = useState([]);
    const [saving,        setSaving]        = useState(false);
    const [isEdit,        setIsEdit]        = useState(false);
    const [editIndex,     setEditIndex]     = useState(null);

    const extractUsers = (text) => {
        const matches = [...text.matchAll(/@([\w.-]+)/g)].map(m => m[1]);
        return matches.map(mention => {
            const u = (usersList || []).find(u => u.label.replace(/\s+/g, '').toLowerCase() === mention.toLowerCase());
            return u ? { id: u.value, name: u.label, image: u.image } : null;
        }).filter(Boolean);
    };

    const autoResize = (ref) => {
        if (ref?.current) {
            ref.current.style.height = 'auto';
            ref.current.style.height = `${ref.current.scrollHeight + 2}px`;
        }
    };

    const onSearch = (e) => {
        const q = e.query.toLowerCase();
        setSuggestions(!q.trim() ? [...(usersList || [])] : (usersList || []).filter(u => u.label.toLowerCase().startsWith(q)));
    };

    const onChange = (e) => {
        setValue(e.target.value);
        setSelectedUsers(extractUsers(e.target.value));
        autoResize(mentionRef);
    };

    const onEditChange = (e) => {
        setEditValue(e.target.value);
        setEditUsers(extractUsers(e.target.value));
        autoResize(editMentionRef);
    };

    const itemTemplate = (s) => <div className="pcq-mention-item">{s.label}</div>;

    const highlightText = (text) => {
        if (!text) return '';
        return text.split(/(@[\w.-]+|https?:\/\/[^\s)]+|\n)/g).map((part, i) => {
            if (/^@[\w.-]+$/.test(part)) return <span key={i} style={{ color: '#2FC623' }}>{part}</span>;
            if (/^https?:\/\/[^\s)]+$/.test(part)) return <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: '#4a90e2' }}>{part}</a>;
            if (part === '\n') return <br key={i} />;
            return part;
        });
    };

    const handleSave = async () => {
        if (!value.trim()) return;
        setSaving(true);
        try {
            const res = await axios.post(`${createComment}${formId}/${userId}`, {
                comment: value, mention: selectedUsers.map(u => ({ id: u.id })), type: 'comment'
            });
            if (res.status === 200) {
                setFeed(prev => [{
                    user_name: user.userName, user_id: user.userId, user_image: user.image,
                    comment_id: res.data.comment_id, type: 'comment',
                    comment: value, mention: selectedUsers, created_at: res.data.created_at, by: user.userName
                }, ...prev]);
                setValue(''); setSelectedUsers([]);
                setTimeout(() => autoResize(mentionRef), 0);
            }
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save comment', life: 3000 });
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = (commentId) => {
        setFeed(prev => prev.filter(c => c.comment_id !== commentId));
        axios.post(`${deleteComment}${commentId}`).catch(() => {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to delete comment', life: 3000 });
        });
    };

    const handleEdit = (idx) => {
        setIsEdit(true); setEditIndex(idx);
        setEditValue(feed[idx].comment);
        setEditUsers(extractUsers(feed[idx].comment));
        setTimeout(() => autoResize(editMentionRef), 0);
    };

    const handleCancelEdit = () => { setIsEdit(false); setEditIndex(null); setEditValue(''); setEditUsers([]); };

    const handleSaveEdit = async (commentId) => {
        const updated = feed.map((c, i) => i === editIndex ? { ...c, comment: editValue, mention: editUsers } : c);
        setFeed(updated); setIsEdit(false); setEditIndex(null); setEditValue(''); setEditUsers([]);
        try {
            await axios.post(`${updateComment}${commentId}`, {
                comment: editValue, mention: editUsers.map(u => ({ id: u.id }))
            });
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to update comment', life: 3000 });
        }
    };

    const stripHtml = (html) => html ? html.replace(/<[^>]*>/g, '').trim() : '';

    const renderAvatar = (name, image) => (
        image
            ? <img src={image} alt={name} className="pcq-av pcq-av-img" />
            : <div className="pcq-av">{getInitials(name)}</div>
    );

    return (
        <div className="pcq-col-activity">
            <p className="pcq-act-head">Activity</p>

            {/* Comment input */}
            <div className="pcq-comment-box">
                {renderAvatar(user.userName, user.image)}
                <div className="pcq-comment-input-wrap">
                    <Mention
                        inputRef={mentionRef}
                        value={value}
                        onChange={onChange}
                        suggestions={suggestions}
                        onSearch={onSearch}
                        field="label"
                        placeholder="Write a comment… @mention the team"
                        rows={1}
                        itemTemplate={itemTemplate}
                        className="pcq-mention-input"
                    />
                    {value.trim() && (
                        <button className="pcq-comment-save" onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving…' : 'Save'}
                        </button>
                    )}
                </div>
            </div>

            {/* Feed */}
            <div className="pcq-feed">
                {feed.length === 0 && <p className="pcq-no-activity">No activity yet.</p>}
                {feed.map((entry, idx) => (
                    <div key={idx} className="pcq-feed-item">
                        {renderAvatar(entry.by || entry.user_name, entry.user_image)}
                        <div className="pcq-feed-body">
                            <div className="pcq-feed-meta">
                                <b>{entry.by || entry.user_name || 'System'}</b>
                                <time>{formatRelativeTime(entry.created_at)}</time>
                            </div>
                            {entry.type === 'call' ? (
                                <div className="pcq-feed-audio">
                                    <CallParticipant contacts={contacts} contactId={entry.contact_id} style={{ fontSize: '11px', color: 'var(--pcq-dim)', marginBottom: '4px' }} />
                                    <audio controls style={{ width: '100%' }}>
                                        <source src={entry.comment} type="audio/mpeg" />
                                    </audio>
                                </div>
                            ) : !isEdit || editIndex !== idx ? (
                                <div className={`pcq-feed-text-box${entry.type === 'audit' ? ' audit' : ''}`}>
                                    {highlightText(stripHtml(entry.comment || entry.text || ''))}
                                    <MentionOthers mentions={entry.mention} />
                                </div>
                            ) : (
                                <div className="pcq-comment-input-wrap" style={{ margin: '6px 0' }}>
                                    <Mention
                                        inputRef={editMentionRef}
                                        value={editValue}
                                        onChange={onEditChange}
                                        suggestions={suggestions}
                                        onSearch={onSearch}
                                        field="label"
                                        placeholder="Edit comment…"
                                        rows={1}
                                        itemTemplate={itemTemplate}
                                        className="pcq-mention-input"
                                    />
                                </div>
                            )}
                            {(entry.user_id === user.userId || isSuperuser) && entry.type === 'comment' && (
                                <div className="pcq-feed-actions">
                                    {!isEdit || editIndex !== idx ? (
                                        <>
                                            <a className="pcq-feed-act" onClick={() => handleDelete(entry.comment_id)}>delete</a>
                                            <a className="pcq-feed-act" onClick={() => handleEdit(idx)}>edit</a>
                                        </>
                                    ) : (
                                        <>
                                            <button className="pcq-feed-act save" onClick={() => handleSaveEdit(entry.comment_id)} disabled={!editValue.trim()}>save</button>
                                            <button className="pcq-feed-act discard" onClick={handleCancelEdit}>Discard Changes</button>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

// ── Card ───────────────────────────────────────────────────
const loanDetailsFields = ["Onedrive Link", "Gdrive Link", "Loan File ID", "Purchase Price", "Loan Amount", "Est. Taxes", "Loan Amount Status", "Closing Date", "Subject Property Address", "Sellers Concession", "First Payment Date", "Insurance Inspection Date"];
const realEstateTransactionFields = ["Real Estate Gdrive Link"];

function PendingClosingCard({ card, userId, toast, onRemove, light, usersList, dragHandleProps, dragStyle, dragRef, isCollapsed, onPopupClose }) {
    const [closingDate, setClosingDate] = useState(
        card.closing_date ? new Date(card.closing_date + 'T00:00:00') : null
    );
    const [pinging,     setPinging]     = useState(false);
    const [dateLoading, setDateLoading] = useState(false);

    const [isOpen,         setIsOpen]         = useState(false);
    const [applicantData,  setApplicantData]  = useState();
    const [popupLoad,      setPopupLoad]      = useState(false);
    const [pipelineId,     setPipelineId]     = useState("");
    const [pipelines,      setPipelines]      = useState([]);
    const [pipelineStages, setPipelineStages] = useState();
    const [allPipelinesData,   setAllPipelinesData]   = useState([]);
    const [inactivePipelineId, setInactivePipelineId] = useState("");
    const [stageIds,           setStageIds]           = useState("");
    const [registeredUsersList,               setRegisteredUsersList]               = useState([]);
    const [loanDetailsCustomFields,           setLoanDetailsCustomFields]           = useState([]);
    const [realEstateTransactionsCustomFields, setRealEstateTransactionsCustomFields] = useState([]);
    const [allCustomFields, setAllCustomFields] = useState([]);
    const { isWorking } = useAppWorkingOn(card);

    const getAllPipelines = () => {
        const url = `${getPipelines}?userId=${userId}`;
        axios.get(url, { headers: { 'Content-Type': 'application/json' } })
            .then((response) => {
                if (response.status === 200) {
                    setAllPipelinesData(response.data);
                    const options = response.data.filter((p) => {
                        if (p.name === "Inactive Apps") { setInactivePipelineId(p.id); return false; }
                        return true;
                    }).map((p) => ({ label: p.name, value: p.id }));
                    setPipelines(options);
                    const item = response.data.find(p => p.id == 1);
                    if (item) setPipelineStages(item.stages);
                }
            });
    };

    const getAllCustomFields = () => {
        axios.get(getCustomFields, { headers: { 'Content-Type': 'application/json' } })
            .then((response) => {
                if (response.status === 200) {
                    setLoanDetailsCustomFields(response.data.filter(d => loanDetailsFields.includes(d.title)));
                    setRealEstateTransactionsCustomFields(response.data.filter(d => realEstateTransactionFields.includes(d.title)));
                    setAllCustomFields(response.data.filter(d => !loanDetailsFields.includes(d.title) && !realEstateTransactionFields.includes(d.title)));
                }
            });
    };

    const handleStageUpdate = (contact_id, form_id, stage, prevStage, emails, isLoad = false, stageId = "") => {
        const payload = { userId, stage_id: stageId ? stageId : stageIds[stage], email_list: emails };
        axios.post(`${UpdateLeadStage}${contact_id}/${form_id}`, payload, { headers: { 'Content-Type': 'application/json' } })
            .then((response) => {
                if (response.status === 200) {
                    toast.current.show({ severity: 'success', summary: 'Success', detail: 'Successfully Updated', life: 3000 });
                } else {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 });
                }
            })
            .catch(() => toast.current.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong', life: 3000 }));
    };

    const openPopupFn = (formId, pid = 1) => {
        setPipelineId(pid);
        setIsOpen(true);
        setPopupLoad(true);
        getAllCustomFields();
        getAllPipelines();
        axios.get(`${appSubmission}${formId}?pipelineId=1`, { headers: { 'Content-Type': 'application/json' } })
            .then((response) => {
                if (response.status === 200) { setApplicantData(response.data); setPopupLoad(false); }
            });
        axios.get(registeredUserList, { headers: { 'Content-Type': 'application/json' } })
            .then((response) => {
                if (response.status === 200) {
                    setRegisteredUsersList(response.data.map(u => ({ label: u.name, value: u.user_id, image: u.image })));
                }
            });
    };

    const handleDateChange = async (date) => {
        setClosingDate(date);
        if (!date) return;
        setDateLoading(true);
        const iso = date.toISOString().split('T')[0];
        try {
            const res = await axios.patch(updateClosingDateQueue, { form_id: card.form_id, closing_date: iso });
            if (res.data.removed) {
                toast.current.show({ severity: 'info', summary: 'Card moved', detail: 'Closing date is now outside this month — card removed from queue.', life: 4000 });
                onRemove(card.form_id);
            } else {
                toast.current.show({ severity: 'success', summary: 'Date updated', detail: `Closing date set to ${formatDisplayDate(iso)}`, life: 3000 });
            }
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to update closing date', life: 3000 });
        } finally {
            setDateLoading(false);
        }
    };

    const downloadXml = async () => {
        try {
            const response = await axios.get(`${downloadXmlFile}${card.form_id}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/xml' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            const name = (card.full_name || 'loan_application').replace(/ /g, '_');
            link.download = `${name}_LOAN_APPLICATION_XML.xml`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch {
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to download XML', life: 3000 });
        }
    };

    const handlePing = async () => {
        setPinging(true);
        try {
            const res = await axios.post(pingClientQueue, { form_id: card.form_id, userId });
            if (res.data.success) {
                const parts = [];
                if (res.data.email_sent) parts.push('email');
                if (res.data.sms_sent)   parts.push('SMS');
                toast.current.show({ severity: 'success', summary: 'Client pinged', detail: `Sent via ${parts.join(' & ') || 'available channels'}`, life: 3500 });
            } else {
                toast.current.show({ severity: 'warn', summary: 'Partial send', detail: res.data.errors?.join(', ') || 'Some channels may have failed', life: 4000 });
            }
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to ping client', life: 3000 });
        } finally {
            setPinging(false);
        }
    };

    const closingDateStr = closingDate ? closingDate.toISOString().split('T')[0] : null;
    const due = dueInfo(closingDateStr);

    if (isCollapsed) {
        return (
            <div className={`pcq-card-collapsed ${isWorking ? WORKING_ON_CLASS : ''}`} ref={dragRef} style={dragStyle}>
                <div className="pcq-name-row">
                    {dragHandleProps && (
                        <div className="pcq-drag-handle" {...dragHandleProps} title="Drag to reorder">⠿</div>
                    )}
                    <div className="pcq-client-name">{card.full_name || '—'}</div>
                </div>
                <WorkingOnChip app={card} />
                {due && <span className={`pcq-due-badge ${due.cls}`} style={{ marginLeft: 'auto' }}>{due.txt}</span>}
            </div>
        );
    }

    return (
        <div className={`pcq-card-row ${isWorking ? WORKING_ON_CLASS : ''}`} ref={dragRef} style={dragStyle}>
            {/* ── Left column ──────────────────────────── */}
            <div className="pcq-col-id">
                <div className="pcq-name-row">
                    {dragHandleProps && (
                        <div className="pcq-drag-handle" {...dragHandleProps} title="Drag to reorder">⠿</div>
                    )}
                    <div className="pcq-client-name">{card.full_name || '—'}</div>
                </div>
                <WorkingOnChip app={card} />
                <div className="pcq-file-id">
                    {(card.customFields || []).find(f => f.name === 'Loan File ID')?.value }
                </div>

                {(() => {
                    const clientStage = (card.current_stages || []).find(s => s.pipeline_name === 'Clients');
                    if (!clientStage) return null;
                    const daysMatch = (clientStage.time_in_stage || '').match(/(\d+)\s*day/);
                    const daysText = daysMatch ? `${daysMatch[1]}d in stage` : null;
                    return (
                        <div className="pcq-stage-row">
                            <span className="pcq-stage-chip">
                                <span className="pcq-stage-name">{clientStage.stage_name.trim()}</span>
                            </span>
                            {daysText && <span className="pcq-stage-time">{daysText}</span>}
                        </div>
                    );
                })()}

                {card.outstanding > 0 && (
                    <div className="pcq-outstanding-badge">
                        <span className="pcq-outstanding-icon">📋</span>
                        {card.outstanding} doc{card.outstanding !== 1 ? 's' : ''} outstanding
                    </div>
                )}

                <div className="pcq-meta-line">
                    <span className="pcq-meta-lbl">Closing</span>
                    <span className="pcq-meta-val">{card.closing_date ? formatDisplayDate(card.closing_date) : '—'}</span>
                    {due && <span className={`pcq-due-badge ${due.cls}`}>{due.txt}</span>}
                </div>

                {/* Avatars + last activity time */}
                {card.images && card.images.length > 0 && (
                    <div className="pcq-avatars-row">
                        <div className="pcq-avatars">
                            {card.images.slice(0, 4).map((img, i) => (
                                <div key={img.id} className="pcq-avatar-wrap" style={{ zIndex: card.images.length - i }} title={img.name}>
                                    {img.image
                                        ? <img src={img.image} alt={img.name} className="pcq-avatar-img" />
                                        : <div className="pcq-avatar-initials">{getInitials(img.name)}</div>
                                    }
                                </div>
                            ))}
                        </div>
                        {card.activity && card.activity.length > 0 && (
                            <span className="pcq-last-active">{formatRelativeTime(card.activity[0].created_at)}</span>
                        )}
                    </div>
                )}

                {/* Financials: Price / Loan / Taxes */}
                {(() => {
                    const findField = (name) => (card.customFields || []).find(f => f.name === name);
                    const price = findField('Purchase Price');
                    const loan  = findField('Loan Amount');
                    const taxes = findField('Est. Taxes');
                    if (!price && !loan && !taxes) return null;
                    return (
                        <div className="pcq-financials">
                            {price && <div className="pcq-fin-item"><span className="pcq-fin-lbl">PRICE</span><span className="pcq-fin-val">{formatCurrency(price.value)}</span></div>}
                            {loan  && <div className="pcq-fin-item"><span className="pcq-fin-lbl">LOAN</span><span className="pcq-fin-val">{formatCurrency(loan.value)}</span></div>}
                            {taxes && <div className="pcq-fin-item"><span className="pcq-fin-lbl">TAXES</span><span className="pcq-fin-val">{formatCurrency(taxes.value)}</span></div>}
                        </div>
                    );
                })()}

                <WorkingOnToggle app={card} toast={toast} compact />

                <div className="pcq-row-actions">
                    <button
                        className="pcq-act-btn primary"
                        onClick={() => openPopupFn(card.form_id, 1)}
                    >
                        Open card
                    </button>
                    {card.phone && (
                        <button
                            className="pcq-act-btn"
                            onClick={() => window.location.href = `tel:${card.phone}`}
                            title={`Call ${card.phone}`}
                        >
                            📞 Call
                        </button>
                    )}
                    <button className="pcq-act-btn" onClick={downloadXml} title="Download loan XML">
                        ⬇ XML
                    </button>
                    {card.queue_user_id && (
                        <button
                            className="pcq-act-btn pcq-act-remove"
                            title="Remove from queue"
                            onClick={async () => {
                                try {
                                    await axios.post(removeQueueItem, {
                                        app_id: card.form_id,
                                        user_ids: [card.queue_user_id],
                                    }, { headers: { 'Content-Type': 'application/json' } });
                                    onRemove(card.form_id);
                                } catch {
                                    toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to remove from queue', life: 3000 });
                                }
                            }}
                        >
                            Remove
                        </button>
                    )}
                </div>
            </div>

            {/* ── Center: checklist ────────────────────── */}
            <ChecklistColumn card={card} onPing={handlePing} pinging={pinging} light={light} userId={userId} toast={toast} usersList={usersList} />

            {/* ── Right: activity ──────────────────────── */}
            <ActivityColumn activity={card.activity || []} contacts={card.contacts} formId={card.form_id} userId={userId} toast={toast} usersList={usersList} />

            <AppModal visible={isOpen} onHide={() => { setIsOpen(false); onPopupClose?.(); }}>
                <div style={{ marginTop: '1rem', minHeight: "70vh" }}>
                    {!popupLoad ? (
                        <AppPopup
                            isFromTable={true}
                            applicantData={applicantData}
                            pipelineId={pipelineId}
                            id={applicantData?.app_status}
                            pipelineStages={pipelineStages}
                            allPipelinesData={allPipelinesData}
                            isFromSearch={true}
                            allCustomFields={allCustomFields}
                            loanDetailsCustomFields={loanDetailsCustomFields}
                            realEstateTransactionsCustomFields={realEstateTransactionsCustomFields}
                            setApplicantData={setApplicantData}
                            isFromOtherPages={true}
                            handleStageUpdate={handleStageUpdate}
                            usersList={registeredUsersList}
                            userId={userId}
                            handleClose={() => setIsOpen(false)}
                            toast={toast}
                        />
                    ) : (
                        <FlexComponent flexDirection="column" justifyContent="center">
                            <div style={{ minWidth: "40vw", height: "100%" }} className='loadingAppItem' />
                        </FlexComponent>
                    )}
                </div>
            </AppModal>
        </div>
    );
}

// ── Sortable card wrapper ──────────────────────────────────
function SortablePCQCard(props) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: String(props.card.form_id),
    });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 999 : undefined,
        position: isDragging ? 'relative' : undefined,
    };
    return (
        <PendingClosingCard
            {...props}
            dragHandleProps={{ ...attributes, ...listeners }}
            dragStyle={style}
            dragRef={setNodeRef}
        />
    );
}


// const FILTERS = ['All', 'Docs Outstanding', 'Due Soon', 'My Files'];
const FILTERS = ['All'];

export default function PendingClosingQueue() {
    const isSuperuser = useSelector(s => s.user.isSuperuser);
    const userId      = useSelector(s => s.user.userId);
    const toast       = useRef(null);

    const [activeId,      setActiveId]      = useState(null);
    const [reorderMode,   setReorderMode]   = useState(false);
    const [cards,         setCards]         = useState([]);
    const [page,          setPage]          = useState(1);
    const [hasMore,       setHasMore]       = useState(true);
    const [total,         setTotal]         = useState(0);
    const [loading,       setLoading]       = useState(true);
    const [loadingMore,   setLoadingMore]   = useState(false);
    const [scope,         setScope]         = useState('my');
    const [filter,        setFilter]        = useState('All');
    const [light,         setLight]         = useState(false);
    const [usersList,     setUsersList]     = useState([]);
    const [softwareUsers, setSoftwareUsers] = useState([]);
    const [queueUserId,   setQueueUserId]   = useState(null);

    const sentinelRef  = useRef(null);
    const fetchingRef  = useRef(false);
    const activeUrlRef = useRef(getQueueItems);
    const abortRef     = useRef(null);
    const paramsRef    = useRef({});

    const now = new Date();
    const monthLabel = now.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

    // ── Main page ──────────────────────────────────────────────
const ADMIN_SCOPE_OPTS = [
    { key: 'my',   label: isSuperuser ? 'Users Queue' : 'Queue' },
    { key: 'this', label: 'This Month' },
    { key: 'next', label: 'Next Month' },
];

    const fetchPage = useCallback(async (pageNum, replace = false) => {
        // Cancel any in-flight request
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        fetchingRef.current = true;
        if (pageNum === 1) setLoading(true); else setLoadingMore(true);
        try {
            const res = await axios.get(activeUrlRef.current, {
                params: { page: pageNum, limit: PAGE_SIZE, ...paramsRef.current },
                signal: controller.signal,
            });
            const data = res.data;
            setCards(prev => replace ? data.results : [...prev, ...data.results]);
            setTotal(data.total);
            setHasMore(data.has_more);
            setPage(pageNum);
        } catch (err) {
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return;
            setHasMore(false);
            toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load queue', life: 3000 });
        } finally {
            fetchingRef.current = false;
            // Only update loading state if the request wasn't aborted,
            // otherwise we'd flash the empty-state before the real fetch completes.
            if (!controller.signal.aborted) {
                if (pageNum === 1) setLoading(false); else setLoadingMore(false);
            }
        }
    }, []);

    const refreshCards = useCallback(async () => {
        try {
            const res = await axios.get(activeUrlRef.current, {
                params: { page: 1, limit: PAGE_SIZE, ...paramsRef.current },
            });
            setCards(res.data.results);
            setTotal(res.data.total);
            setHasMore(res.data.has_more);
            setPage(1);
        } catch {}
    }, []);

    useEffect(() => {
        if (userId === null) return;
        if (!isSuperuser) {
            // Non-superuser: load their own queue directly
            activeUrlRef.current = getQueueItems;
            paramsRef.current = { userId: userId };
            fetchPage(1, true);
            return;
        }
        // Superuser: default to "Users Queue" with no user filter
        activeUrlRef.current = getQueueItems;
        paramsRef.current = {};
        fetchPage(1, true);
    }, [isSuperuser, userId, fetchPage]);

    useEffect(() => {
        if (isSuperuser) {
            axios.get(getSoftwareUsers).then(res => {
                if (res.status === 200) {
                    setSoftwareUsers(res.data.map(u => ({
                        label: u.full_name,
                        value: u.id,
                        image: u.image,
                    })));
                }
            }).catch(() => {});
        }
    }, [isSuperuser]);

    useEffect(() => {
        if (!sentinelRef.current) return;
        const observer = new IntersectionObserver(
            entries => {
                if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
                    fetchPage(page + 1);
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loading, page, fetchPage]);

    useEffect(() => {
        axios.get(registeredUserList).then(res => {
            if (res.status === 200) {
                setUsersList(res.data.map(u => ({
                    label: u.name,
                    value: u.user_id,
                    image: u.image,
                    role: u.role || u.designation || '',
                })));
            }
        }).catch(() => {});
    }, []);

    const handleRemove = (formId) => {
        setCards(prev => prev.filter(c => c.form_id !== formId));
        setTotal(prev => prev - 1);
    };

    const handleScope = (key) => {
        setScope(key);
        if (key === 'my') {
            activeUrlRef.current = getQueueItems;
            paramsRef.current = queueUserId ? { userId: queueUserId } : {};
        } else if (key === 'next') {
            activeUrlRef.current = projectedClosingsQueue;
            paramsRef.current = {};
        } else {
            activeUrlRef.current = pendingClosingsQueue;
            paramsRef.current = {};
        }
        fetchPage(1, true);
    };

    const handleQueueUserChange = (uid) => {
        setQueueUserId(uid);
        activeUrlRef.current = getQueueItems;
        paramsRef.current = uid ? { userId: uid } : {};
        fetchPage(1, true);
    };

    const canDrag = scope === 'my' && (!isSuperuser || !!queueUserId);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const handleDragEnd = async ({ active, over }) => {
        setActiveId(null);
        if (!over || active.id === over.id) return;
        const sourceFormId = Number(active.id);
        const targetFormId = Number(over.id);
        const oldIndex = cards.findIndex(c => c.form_id === sourceFormId);
        const newIndex = cards.findIndex(c => c.form_id === targetFormId);
        if (oldIndex === -1 || newIndex === -1) return;
        const prevCards = [...cards];
        setCards(arrayMove([...cards], oldIndex, newIndex));
        const payload = {
            source_app_id: sourceFormId,
            target_app_id: targetFormId,
            insert_before: newIndex < oldIndex,
            user_id: cards[oldIndex]?.queue_user_id || (isSuperuser ? queueUserId : userId),
        };
        try {
            await axios.patch(reorderQueue, payload, { headers: { 'Content-Type': 'application/json' } });
        } catch {
            setCards(prevCards);
            toast.current?.show({ severity: 'error', summary: 'Reorder failed', detail: 'Changes reverted', life: 3000 });
        }
    };

    return (
        <div className="pcq-page">
            <Toast ref={toast} />

            {/* ── Control bar ─────────────────────────── */}
            <div className="pcq-controlbar">
                {/* {isSuperuser && ( */}
                    <div className="pcq-scope-btns">
                        {ADMIN_SCOPE_OPTS.map(o => (
                            <button
                                key={o.key}
                                className={`pcq-scope-btn${scope === o.key ? ' active' : ''}`}
                                onClick={() => handleScope(o.key)}
                            >
                                {o.label}
                            </button>
                        ))}
                    </div>
                {/* )} */}
                {isSuperuser && scope === 'my' && (
                    <Dropdown
                        value={queueUserId}
                        options={softwareUsers}
                        onChange={e => handleQueueUserChange(e.value)}
                        placeholder="All users"
                        showClear
                        filter
                        className="pcq-user-dropdown"
                        panelClassName="pcq-user-dropdown-panel"
                        optionLabel="label"
                    />
                )}
                <div className="pcq-cb-spacer" />
                <div className="pcq-filterbar">
                    {FILTERS.map(f => (
                        <button
                            key={f}
                            className={`pcq-chip${filter === f ? ' active' : ''}`}
                            onClick={() => setFilter(f)}
                        >
                            {f}
                        </button>
                    ))}
                    <button
                        className={`pcq-chip${light ? ' active' : ''}`}
                        onClick={() => setLight(l => !l)}
                        title="Toggle checklist item background"
                    >
                        {light ? '🌙 Dark items' : '☀️ Light items'}
                    </button>
                    {canDrag && cards.length > 0 && (
                        <button
                            className={`pcq-chip${reorderMode ? ' active' : ''}`}
                            onClick={() => setReorderMode(m => !m)}
                            title={reorderMode ? 'Exit reorder mode' : 'Collapse cards to reorder'}
                        >
                            ⠿ Reorder
                        </button>
                    )}
                    {/* <button className="pcq-chip" onClick={() => fetchPage(1, true)}>↺ Refresh</button> */}
                </div>
            </div>

            {/* ── Hitlist banner ───────────────────────── */}
            {!loading && cards.length > 0 && <HitlistBanner cards={cards} />}

            {/* ── Queue ────────────────────────────────── */}
            <div className="pcq-queue">
                {loading && [0, 1, 2].map(i => <div key={i} className="pcq-skeleton" />)}

                {!loading && cards.length === 0 && (
                    <div className="pcq-empty">
                        <span>📭</span>
                        <p>No Queue items available.</p>
                    </div>
                )}

                {!loading && (canDrag ? (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCorners}
                        onDragStart={({ active }) => setActiveId(active.id)}
                        onDragEnd={handleDragEnd}
                        onDragCancel={() => setActiveId(null)}
                    >
                        <SortableContext items={cards.map(c => String(c.form_id))} strategy={verticalListSortingStrategy}>
                            {cards.map(card => (
                                <SortablePCQCard
                                    key={card.form_id}
                                    card={card}
                                    userId={userId}
                                    toast={toast}
                                    onRemove={handleRemove}
                                    light={light}
                                    usersList={usersList}
                                    isCollapsed={reorderMode || activeId !== null}
                                    onPopupClose={refreshCards}
                                />
                            ))}
                        </SortableContext>
                        <DragOverlay>
                            {activeId ? (() => {
                                const dragged = cards.find(c => String(c.form_id) === activeId);
                                return (
                                    <div className="pcq-card-drag-overlay">
                                        <span className="pcq-drag-handle">⠿</span>
                                        <span className="pcq-client-name">{dragged?.full_name || '—'}</span>
                                    </div>
                                );
                            })() : null}
                        </DragOverlay>
                    </DndContext>
                ) : (
                    cards.map(card => (
                        <PendingClosingCard
                            key={card.form_id}
                            card={card}
                            userId={userId}
                            toast={toast}
                            onRemove={handleRemove}
                            light={light}
                            usersList={usersList}
                            onPopupClose={refreshCards}
                        />
                    ))
                ))}
            </div>

            <div ref={sentinelRef} style={{ height: 1 }} />

            {loadingMore && (
                <div className="pcq-loading-more">⏳ Loading more…</div>
            )}

            {!loading && !hasMore && cards.length > 0 && (
                <p className="pcq-all-loaded">
                    All {total} file{total !== 1 ? 's' : ''} loaded
                </p>
            )}
        </div>
    );
}
